<template>
  <b-modal
      id="modal"
      no-close-on-backdrop
      centered
      visible
      :size="size || (large ? 'lg' : 'md')"
      :dialog-class="dialogClass"
      header-class="text-white p-2 modal-background"
      body-class="p-0"
      :hide-footer="!hasFooter"
      @hide="hiding"
      @hidden="hidden"
      @shown="shown"
      @show="show"
  >
    <template #modal-header>
      <b-overlay :show="$store.getters.activity || busy" :opacity="0" class="w-100" spinner-type="none">
        <div class="d-flex flex-row w-100">
          <div class="modal-title flex-fill ml-1 py-1 h6">
            <slot name="title">{{ title }}</slot>
          </div>
          <slot name="buttons"/>
          <div style="width: 10px"/>
          <button type="button" class="close text-white m-0 p-0" @click="close">
            <font-awesome-icon icon="fa-solid fa-window-close" class="pr-1"/>
          </button>
        </div>
      </b-overlay>
    </template>

    <template #default>
      <b-overlay :show="$store.getters.activity || busy" spinner-variant="danger" opacity="0.4">
        <div class="d-flex flex-column">
          <div v-if="hasTop && !hideTop" :class="topClass" id="modal-top">
            <slot name="top" v-if="$slots.top"/>
            <portal-target :name="portals.top" v-else/>
          </div>

          <div :style="style" class="d-flex flex-column flex-fill">
            <div v-if="hasSubTop" :class="subTopClass" id="modal-subtop">
              <slot name="subtop" v-if="$slots.subtop"/>
              <portal-target :name="portals.subtop" v-else/>
            </div>

            <div :class="'flex-fill ' + bodyClass + scroll" id="vue-modal-body">
              <slot/>
            </div>
          </div>

          <div v-if="hasBottom" :class="bottomClass" id="modal-bottom">
            <slot name="bottom" v-if="$slots.bottom"/>
            <portal-target :name="portals.bottom" v-else/>
          </div>
        </div>
      </b-overlay>
    </template>

    <template #modal-footer>
      <b-overlay class="w-100 text-right" :show="$store.getters.activity || busy" spinner-type="none" opacity="0.4">
        <slot name="footer"/>
        <portal-target :name="portals.footer"/>
      </b-overlay>
    </template>
  </b-modal>
</template>

<script>
import {Wormhole} from "portal-vue";
import {bus} from "@/lib/bus";
import {breakpointsBootstrapV5, useBreakpoints} from "@vueuse/core/index";

export default {
  name: "modal",

  setup() {
    const breakpoints = useBreakpoints(breakpointsBootstrapV5)
    const is_small = breakpoints.isSmallerOrEqual('sm')

    return {is_small}
  },

  props: {
    title: String,
    height: String,
    large: Boolean,
    size: String,
    /** a class on the dialog itself, for a width of its own */
    dialogClass: String,
    noScroll: Boolean,
    hideTop: Boolean,
    bodyClass: {
      type: String,
      default: 'px-2 pb-4 pt-2'
    },
    topClass: {
      type: String,
      default: 'p-2 border-bottom'
    },
    subTopClass: {
      default: 'pt-2 px-2',
      type: String
    },
    bottomClass: {
      type: String,
      default: 'p-2 border-top'
    },
    onClose: Function,
    busy: Boolean,
    useMaxHeight: Boolean,
    closeData: null
  },

  // Every modal has its own portal targets. With one shared name, a modal opened over
  // another took the first one's top strip and footer buttons (portal-vue hands a name's
  // content to the newest target). Children reach them through modalTarget('footer') etc.
  provide() {
    return {modalPortals: this.portals}
  },

  data() {
    return {
      closeit: false,
      overlay: 0,
    }
  },

  methods: {
    hidden() {
      this.$root.$emit('modal-close')
      this.$root.$emit('submit', this.closeData)
      bus.$emit('modal-close')
    },

    shown() {
      this.$emit('shown')
      bus.$emit('modal-shown')
    },

    show() {
      this.$emit('show')
      bus.$emit('modal-show')
    },

    close() {
      this.$bvModal.hide('modal')
    },

    async hiding(e) {
      if (!this.closeit && typeof this.onClose === 'function') {
        e.preventDefault()
        if (await this.onClose()) {
          this.closeit = true
          this.close()
        }
      }
    },
  },

  computed: {
    portals() {
      const id = this._uid
      return {top: 'modal-top-' + id, subtop: 'modal-subtop-' + id, bottom: 'modal-bottom-' + id, footer: 'modal-footer-' + id}
    },

    hasFooter() {
      return !!this.$slots['footer'] || Wormhole.hasContentFor(this.portals.footer)
    },

    hasSubTop() {
      return !!this.$slots['subtop'] || Wormhole.hasContentFor(this.portals.subtop)
    },

    hasTop() {
      return !!this.$slots['top'] || Wormhole.hasContentFor(this.portals.top)
    },

    hasBottom() {
      return !!this.$slots['bottom'] || Wormhole.hasContentFor(this.portals.bottom)
    },

    style() {
      if (this.height) {
        return this.is_small || this.noScroll ?
            {'min-height': this.height} :
            {[this.useMaxHeight ? 'max-height' : 'height']: this.height}
      }
      return ' '
    },

    scroll() {
      if (this.height) {
        return this.is_small || this.noScroll ?
            ' ' : ' overflow-auto'
      }
      return ' '
    }
  },
  mounted() {
    this.$store.commit('activity_loading', 'modal-shown')
  },
  beforeDestroy() {
    this.$store.commit('activity_done', 'modal-shown')
  }
}
</script>

<style scoped>

</style>